/* eslint-disable max-lines-per-function */
import { SvgIcon } from '@bob/library'
import { AppProviderContext } from '@websrc/App/AppContext'
import Heading from '@websrc/App/Components/Heading/Heading'
import { trackSubmitForm } from '@websrc/App/Helpers/tracking'
import { useLocale } from '@websrc/App/Hooks/useLocale'
import { useScript } from '@websrc/App/Hooks/useScript/useScript'
import { matomo } from '@websrc/App/Tracking/Matomo'
import { mapForm } from '@websrc/DataMappers/mapSpot'
import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { PageMessage } from '../PageMessage/PageMessage'
import { styledForm } from './Form.css'

declare global {
  interface Window {
    d365mktforms: any
  }
}

export const RealTimeForm: FC<
  Omit<ReturnType<typeof mapForm>, 'script' | 'realTimeFormScript'> & {
    realTimeFormScript: string
  }
> = ({
  description,
  headline,
  realTimeFormScript,
  tracking_enabled,
  action_on_submit,
  submit_page_message,
  submit_redirect_url,
}) => {
  const locale = useLocale()
  const { matomoPageTemplate } = useContext(AppProviderContext)
  const [showPageMessage, setShowPageMessage] = useState(false)
  const pageMessageRef = useRef<HTMLDivElement>(null)
  const scriptLoaded = useScript(
    'https://cxppusa1formui01cdnsa01-endpoint.azureedge.net/global/FormLoader/FormLoader.bundle.js'
  )

  const findInputByLabelText = useCallback((labelText: string) => {
    const labels = document.querySelectorAll('label[title="' + labelText + '"]')
    for (var i = 0; i < labels.length; i++) {
      var label = labels[i]
      var inputId = label.getAttribute('for')
      if (inputId) {
        const inputElement = document.querySelector('input#' + inputId) as HTMLInputElement
        if (inputElement) {
          return inputElement
        }
      }
    }
    return null
  }, [])

  const populateURLBuildField = useCallback(() => {
    const params = new URLSearchParams(document.location.search)
    const URLbuildField = findInputByLabelText('URL Build ID')

    if (URLbuildField) {
      URLbuildField.value = params.get('mtm_cid') ?? ''
    }
  }, [findInputByLabelText])

  const populateReferralField = useCallback(() => {
    const referralUrlField = findInputByLabelText('Referral URL')
    if (referralUrlField) {
      referralUrlField.value = document.referrer
    }
  }, [findInputByLabelText])

  const handleSubmit = useCallback(() => {
    if (action_on_submit === 'Show message') {
      const offsetTop = pageMessageRef.current?.offsetTop
      window.scrollTo({ top: offsetTop, behavior: 'smooth' })
      setShowPageMessage(true)
    } else if (action_on_submit === 'Redirect to a new page') {
      const redirectUrl = submit_redirect_url?.startsWith('/')
        ? submit_redirect_url.slice(1)
        : `${submit_redirect_url}`
      if (redirectUrl) {
        window.location.href = `/${locale}/${redirectUrl}`
      }
    }
  }, [action_on_submit, locale, submit_redirect_url])

  const mapFormScriptData = useMemo(() => {
    const findDivElements = realTimeFormScript.match(/<div.*?>.*?<\/div>/gs)
    // if the div is not found, return null
    if (findDivElements?.length !== 1) {
      return null
    }

    const formId = findDivElements[0].match(/data-form-id\s*=\s*["'](.*?)["']/)?.[1]
    const formApiUrl = findDivElements[0].match(/data-form-api-url\s*=\s*["'](.*?)["']/)?.[1]
    const cachedFormUrl = findDivElements[0].match(/data-cached-form-url\s*=\s*["'](.*?)["']/)?.[1]
    const dataReadableEventId = findDivElements[0].match(
      /data-readable-event-id\s*=\s*["'](.*?)["']/
    )?.[1]

    // if the script is not valid, return null
    if (!formId || !formApiUrl || !cachedFormUrl) {
      return null
    }

    return {
      formId,
      formApiUrl,
      cachedFormUrl,
      dataReadableEventId,
    }
  }, [realTimeFormScript])

  // add the script to the DOM
  useEffect(() => {
    if (mapFormScriptData) {
      const afterformsubmitHandler = () => {
        handleSubmit()
        tracking_enabled && trackSubmitForm()
        matomo.trackEvent('conversion', matomoPageTemplate, ['form'], [headline], 'form')
      }
      const afterformloadHandler = () => {
        populateURLBuildField()
        populateReferralField()
      }

      document.addEventListener('d365mkt-afterformsubmit', afterformsubmitHandler)
      document.addEventListener('d365mkt-afterformload', afterformloadHandler)

      return () => {
        document.removeEventListener('d365mkt-afterformsubmit', afterformsubmitHandler)
        document.removeEventListener('d365mkt-afterformload', afterformloadHandler)
      }
    }
  }, [
    mapFormScriptData,
    handleSubmit,
    tracking_enabled,
    matomoPageTemplate,
    headline,
    populateURLBuildField,
    populateReferralField,
  ])

  const showSubmitMessage = showPageMessage && submit_page_message

  return (
    <div ref={pageMessageRef}>
      {showSubmitMessage ? (
        <PageMessage message={{ ...submit_page_message }} />
      ) : (
        <>
          <style>
            {`
              .realTimeForm .outer{
                width: 100% !important;
              }

              .realTimeForm .outer th{
                padding: 0 !important;
              }
              .realTimeForm .marketingForm th.columnContainer{
                padding: 0 15px !important;
              }
              .realTimeForm .marketingForm th.columnContainer.inner{
                padding: 0 !important;
              }

              .realTimeForm .textFormFieldBlock, 
              .realTimeForm .dateTimeFormFieldBlock, 
              .realTimeForm .twoOptionFormFieldBlock, 
              .realTimeForm .optionSetFormFieldBlock, 
              .realTimeForm .multiOptionSetFormFieldBlock, 
              .realTimeForm .lookupFormFieldBlock{
                padding: 0 0;
                margin: 0 0 12px 0;
                gap: 4px;
              }

              .realTimeForm table.multi .textFormFieldBlock, 
              .realTimeForm table.multi .dateTimeFormFieldBlock, 
              .realTimeForm table.multi .twoOptionFormFieldBlock, 
              .realTimeForm table.multi .optionSetFormFieldBlock, 
              .realTimeForm table.multi .multiOptionSetFormFieldBlock, 
              .realTimeForm table.multi .lookupFormFieldBlock {
                padding: 0;
              }

              .realTimeForm .textFormFieldBlock label, 
              .realTimeForm .dateTimeFormFieldBlock label, 
              .realTimeForm .lookupFormFieldBlock label, 
              .realTimeForm .twoOptionFormFieldBlock label.block-label, 
              .realTimeForm .optionSetFormFieldBlock label.block-label, 
              .realTimeForm .multiOptionSetFormFieldBlock label.block-label, 
              .realTimeForm div[data-editorblocktype="Captcha"] label[id^="wlspispHipInstructionContainer"], 
              .realTimeForm .textFormFieldBlock label *, 
              .realTimeForm .dateTimeFormFieldBlock label *, 
              .realTimeForm .lookupFormFieldBlock label *, 
              .realTimeForm .twoOptionFormFieldBlock label.block-label *, 
              .realTimeForm .optionSetFormFieldBlock label.block-label *, 
              .realTimeForm .multiOptionSetFormFieldBlock label.block-label *, 
              .realTimeForm .eventSession label *, 
              .realTimeForm .consentBlock label p {  
                font-size: 14px;
                line-height: 18px;
                letter-spacing: -0.5px !important;
                color: #000;  
                font-weight: 400;
                font-family: CeraPro, Verdana, Sans-Serif;
              }

              .realTimeForm .optionSetFormFieldBlock select, 
              .realTimeForm .textFormFieldBlock input, 
              .realTimeForm .dateTimeFormFieldBlock input, 
              .realTimeForm .lookupFormFieldBlock input, 
              .realTimeForm div[data-editorblocktype="Captcha"] input {
                height: 48px;
                border-radius: 4px;
                border: 1px solid #BABABA;
                font-size: 16px;
                padding: 12px 16px;
                text-indent: 0px;
                background-color: #fff;
              }

              .realTimeForm .marketingForm textarea {
                border-radius: 4px;
                border: 1px solid #BABABA;
                font-size: 16px;
                padding: 12px 16px;
                text-indent: 0px;
                resize: vertical;
                background-color: #fff;
              }

              .realTimeForm .marketingForm .primaryButton, 
              .realTimeForm .submitButton {
                background-color: #006cb7;
                border: none;
                border-radius: 0;
                color: #fff;
                cursor: pointer;
                font-size: 16px;
                font-weight: 700;
                letter-spacing: -.6px;
                line-height: 22px;
                padding: 15px 25px;
                text-transform: uppercase;
              }

              .realTimeForm .marketingForm .primaryButton:hover, 
              .realTimeForm .submitButton:hover {
                background-color: #00528c
              }

              div[data-editorblocktype="Text"] {
                margin-left: -10px;
              }

              .realTimeForm a{
                color: #006cb7 !important;
              }
              .realTimeForm a:hover{
                color: #00528c  !important;
              }
                      
              .realTimeForm div[data-editorblocktype="Divider"] {
                display: none;
              }
              .realTimeForm div[data-editorblocktype="Divider"] + div[data-editorblocktype="Text"] {
                margin-top: 30px !important;
                margin-bottom: 10px !important;
              }

              .realTimeForm .consentBlock{
                padding: 0 !important;
                margin: 15px 0 5px;
              }
              .realTimeForm .consentBlock + div[data-editorblocktype="Text"]{
                margin: 0 0 15px 30px !important;
              }
              .realTimeForm .consentBlock + div[data-editorblocktype="Text"] *{
                font-size: 12px !important;
                letter-spacing: -.4px !important;
                line-height: 18px !important;
              }
              .realTimeForm .consentBlock + div[data-editorblocktype="Text"] *:not(a, a > span){
                color: #555 !important;
              }

              .realTimeForm .twoOptionFormFieldBlock div.radiobuttons>div, 
              .realTimeForm .twoOptionFormFieldBlock div.twooption_checkbox>div, 
              .realTimeForm .optionSetFormFieldBlock div.radiobuttons>div{
                aling-items: top;
              }

              .realTimeForm .textFormFieldBlock[data-required="required"] label::after, 
              .realTimeForm .twooption_checkbox[data-required="required"] label::after, 
              .realTimeForm .dateTimeFormFieldBlock[data-required="required"] label::after, 
              .realTimeForm .lookupFormFieldBlock[data-required="required"] label::after, 
              .realTimeForm .twoOptionFormFieldBlock[data-required="required"] label.block-label::after, 
              .realTimeForm .twoOptionFormFieldBlock[data-required="required"] div.twooption_checkbox label::after, 
              .realTimeForm .optionSetFormFieldBlock[data-required="required"] label.block-label::after, 
              .realTimeForm .multiOptionSetFormFieldBlock[data-required="required"] label.block-label::after, 
              .realTimeForm .consentBlock[data-required="required"] label p:first-of-type::after, 
              .realTimeForm .eventSession[data-required="required"] label::after, 
              .realTimeForm div[data-editorblocktype="Captcha"][data-required="required"] label[id^="wlspispHipInstructionContainer"]::after{
                display: none;
              }
              .realTimeForm .textFormFieldBlock label::after, 
              .realTimeForm .twooption_checkbox label::after, 
              .realTimeForm .dateTimeFormFieldBlock label::after, 
              .realTimeForm .lookupFormFieldBlock label::after, 
              .realTimeForm .twoOptionFormFieldBlock label.block-label::after, 
              .realTimeForm .twoOptionFormFieldBlock div.twooption_checkbox label::after, 
              .realTimeForm .optionSetFormFieldBlock label.block-label::after, 
              .realTimeForm .multiOptionSetFormFieldBlock label.block-label::after, 
              .realTimeForm .consentBlock label p:first-of-type::after, 
              .realTimeForm .eventSession label::after, 
              .realTimeForm div[data-editorblocktype="Captcha"] label[id^="wlspispHipInstructionContainer"]::after{
                content: ' (Optional)';
                color: #555 !important;
                visibility: visible;
                text-align: left;
                width: auto;
                line-height: inherit;
                margin-left: 5px;
              }
            `}
          </style>

          <div className={styledForm._}>
            <div className={styledForm.mainText._}>
              <Heading description={description} headline={headline} level={1} size={'xl'} />
            </div>
            <div className={'realTimeForm'}>
              {mapFormScriptData !== null ? (
                // todo remove the div and use the FormPlaceholder component after MS does an update in the form loader script
                // for handling the readable event id in the FormPlaceholder component
                mapFormScriptData?.dataReadableEventId ? (
                  <div
                    data-cached-form-url={mapFormScriptData?.cachedFormUrl}
                    data-form-api-url={mapFormScriptData?.formApiUrl}
                    data-form-id={mapFormScriptData?.formId}
                    data-readable-event-id={mapFormScriptData?.dataReadableEventId}
                  />
                ) : (
                  scriptLoaded === 'loaded' &&
                  React.createElement(
                    window.d365mktforms.FormPlaceholder,
                    {
                      formId: mapFormScriptData?.formId,
                      formApiBaseUrl: mapFormScriptData?.formApiUrl,
                      formUrl: mapFormScriptData?.cachedFormUrl,
                    },
                    null
                  )
                )
              ) : (
                <div className={styledForm.form.error}>
                  <SvgIcon color={'error'} name={'WARNING'} size={20} />
                  Oops! Looks like this form is missing or not set up correctly.
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
